import {
  escapeContentsLine,
  formatMarkdownTable,
  parseContentsLine,
  parseMarkdownTable,
  TABLE_OF_CONTENTS_MARKER,
} from '@noti/shared';
import type { JSONContent } from '@tiptap/core';

function textOf(node: JSONContent | undefined): string {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'mention') {
    const name = node.attrs?.['name'];
    return `@${typeof name === 'string' ? name : ''}`;
  }
  return (node.content ?? []).map((child) => textOf(child)).join('');
}

function paragraph(text: string): JSONContent {
  if (!text) return { type: 'paragraph' };
  return { type: 'paragraph', content: [{ type: 'text', text }] };
}

function blockToMarkdown(node: JSONContent): string {
  switch (node.type) {
    case 'tableOfContents':
      return TABLE_OF_CONTENTS_MARKER;
    case 'table':
      return formatMarkdownTable(
        (node.content ?? []).map((row) => (row.content ?? []).map(textOf)),
      );
    case 'heading': {
      const level = node.attrs?.['level'];
      const marks = '#'.repeat(typeof level === 'number' ? Math.min(Math.max(level, 1), 4) : 1);
      return `${marks} ${textOf(node)}`.trimEnd();
    }
    case 'bulletList':
      return (node.content ?? []).map((item) => `- ${textOf(item)}`).join('\n');
    case 'orderedList':
      return (node.content ?? []).map((item, index) => `${index + 1}. ${textOf(item)}`).join('\n');
    case 'taskList':
      return (node.content ?? [])
        .map((item) => `${item.attrs?.['checked'] === true ? '- [x]' : '- [ ]'} ${textOf(item)}`)
        .join('\n');
    case 'blockquote':
      return `> ${textOf(node)}`;
    case 'codeBlock':
      return `\`\`\`\n${textOf(node)}\n\`\`\``;
    case 'horizontalRule':
      return '---';
    case 'callout': {
      const icon = typeof node.attrs?.['icon'] === 'string' ? node.attrs['icon'] : '';
      const lines = (node.content ?? [])
        .map((child) => blockToMarkdown(child))
        .filter((line) => line.length > 0)
        .join('\n')
        .split('\n');
      return lines
        .map((line, index) => (index === 0 ? `> ${icon} ${line}`.trimEnd() : `> ${line}`))
        .join('\n');
    }
    default:
      return textOf(node);
  }
}

/** Body only. The page title lives in its own field, so it is not a heading here. */
export function documentToMarkdown(doc: JSONContent): string {
  return (doc.content ?? [])
    .map((node) =>
      node.type === 'paragraph' ? escapeContentsLine(blockToMarkdown(node)) : blockToMarkdown(node),
    )
    .filter((block) => block.length > 0)
    .join('\n\n');
}

type LineKind =
  | { kind: 'contents' }
  | { kind: 'blank' }
  | { kind: 'fence' }
  | { kind: 'rule' }
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'task'; checked: boolean; text: string }
  | { kind: 'bullet'; text: string }
  | { kind: 'ordered'; text: string }
  | { kind: 'quote'; text: string }
  | { kind: 'text'; text: string };

function classify(line: string): LineKind {
  if (line.trim() === '') return { kind: 'blank' };
  const contents = parseContentsLine(line);
  if (contents) return contents;
  if (line.startsWith('```')) return { kind: 'fence' };
  if (line.trim() === '---') return { kind: 'rule' };
  const heading = /^(#{1,4}) (.*)$/.exec(line);
  if (heading) return { kind: 'heading', level: heading[1]?.length ?? 1, text: heading[2] ?? '' };
  const task = /^- \[([ xX])\] (.*)$/.exec(line);
  if (task) {
    return { kind: 'task', checked: (task[1] ?? '').toLowerCase() === 'x', text: task[2] ?? '' };
  }
  const bullet = /^- (.*)$/.exec(line);
  if (bullet) return { kind: 'bullet', text: bullet[1] ?? '' };
  const ordered = /^\d+\. (.*)$/.exec(line);
  if (ordered) return { kind: 'ordered', text: ordered[1] ?? '' };
  const quote = /^> (.*)$/.exec(line);
  if (quote) return { kind: 'quote', text: quote[1] ?? '' };
  return { kind: 'text', text: line };
}

type Pending =
  | { kind: 'bullet'; items: string[] }
  | { kind: 'ordered'; items: string[] }
  | { kind: 'task'; items: { text: string; checked: boolean }[] }
  | { kind: 'code'; lines: string[] };

function listItem(text: string): JSONContent {
  return { type: 'listItem', content: [paragraph(text)] };
}

function flush(pending: Pending | null, out: JSONContent[]): null {
  if (!pending) return null;
  if (pending.kind === 'bullet') {
    out.push({ type: 'bulletList', content: pending.items.map(listItem) });
  } else if (pending.kind === 'ordered') {
    out.push({ type: 'orderedList', content: pending.items.map(listItem) });
  } else if (pending.kind === 'task') {
    out.push({
      type: 'taskList',
      content: pending.items.map((item) => ({
        type: 'taskItem',
        attrs: { checked: item.checked },
        content: [paragraph(item.text)],
      })),
    });
  } else {
    const text = pending.lines.join('\n');
    out.push(
      text ? { type: 'codeBlock', content: [{ type: 'text', text }] } : { type: 'codeBlock' },
    );
  }
  return null;
}

function pushLine(kind: LineKind, pending: Pending | null, out: JSONContent[]): Pending | null {
  if (kind.kind === 'bullet') {
    const next = pending?.kind === 'bullet' ? pending : { kind: 'bullet' as const, items: [] };
    if (next !== pending) flush(pending, out);
    next.items.push(kind.text);
    return next;
  }
  if (kind.kind === 'ordered') {
    const next = pending?.kind === 'ordered' ? pending : { kind: 'ordered' as const, items: [] };
    if (next !== pending) flush(pending, out);
    next.items.push(kind.text);
    return next;
  }
  if (kind.kind === 'task') {
    const next = pending?.kind === 'task' ? pending : { kind: 'task' as const, items: [] };
    if (next !== pending) flush(pending, out);
    next.items.push({ text: kind.text, checked: kind.checked });
    return next;
  }
  flush(pending, out);
  if (kind.kind === 'contents') {
    out.push({ type: 'tableOfContents' });
  } else if (kind.kind === 'heading') {
    out.push({
      type: 'heading',
      attrs: { level: kind.level },
      ...(kind.text ? { content: [{ type: 'text', text: kind.text }] } : {}),
    });
  } else if (kind.kind === 'quote') {
    out.push({ type: 'blockquote', content: [paragraph(kind.text)] });
  } else if (kind.kind === 'rule') {
    out.push({ type: 'horizontalRule' });
  } else if (kind.kind === 'text') {
    out.push(paragraph(kind.text));
  }
  return null;
}

export function markdownToDocument(markdown: string): JSONContent {
  const content: JSONContent[] = [];
  let pending: Pending | null = null;
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';
    const table = pending?.kind === 'code' ? null : parseMarkdownTable(lines, index);
    if (table) {
      pending = flush(pending, content);
      content.push({
        type: 'table',
        content: table.rows.map((row, rowIndex) => ({
          type: 'tableRow',
          content: row.map((text) => ({
            type: rowIndex === 0 ? 'tableHeader' : 'tableCell',
            content: [paragraph(text)],
          })),
        })),
      });
      index += table.lineCount - 1;
      continue;
    }
    if (pending?.kind === 'code') {
      if (line.startsWith('```')) pending = flush(pending, content);
      else pending.lines.push(line);
      continue;
    }
    const kind = classify(line);
    if (kind.kind === 'fence') {
      flush(pending, content);
      pending = { kind: 'code', lines: [] };
      continue;
    }
    if (kind.kind === 'blank') {
      pending = flush(pending, content);
      continue;
    }
    pending = pushLine(kind, pending, content);
  }
  flush(pending, content);
  if (content.length === 0) content.push({ type: 'paragraph' });
  return { type: 'doc', content };
}
